<script>

    import * as echarts from 'echarts'
    import Moment from 'moment'
    import { onDestroy } from 'svelte'

    import StatisticsService from '../$services/statistics.service'
    import NumeralMethods from '../$methods/numeral.methods'
    import StatsCard from '../$components/stats.card.svelte'
    import Spinner from '../$components/spinner.svelte'
    import Tabs from '../$components/tabs.svelte'

    export let dateStart = null
    export let dateEnd = null

    const SERIES_COLORS = {
        'quotation.create': '#3b82f6',
        'shipment.create': '#22c55e',
        'shipment.error': '#ef4444',
        'payment.success': '#0ea5e9',
    }

    const GROUP_TABS = [
        { key: 'all', name: 'Todos' },
        { key: 'shipment', name: 'Envíos' },
        { key: 'payment', name: 'Pagos' },
        { key: 'engagement', name: 'Engagement' },
    ]

    let loading = false
    let error = null
    let highlights = null
    let activities = []
    let series = null
    let groupFilter = 'all'
    let chartContainer
    let chart

    $: periodLabel = dateStart && dateEnd
        ? `${Moment(dateStart).locale('es').format('D MMM YYYY')} - ${Moment(dateEnd).locale('es').format('D MMM YYYY')}`
        : ''

    $: highlightCards = highlights ? [
        highlights.quotations,
        highlights.shipments,
        highlights.shipmentErrors,
        highlights.payments,
    ].filter(Boolean).map((metric) => ({
        title: metric.label,
        value: NumeralMethods.number(metric.value ?? 0),
        change: metric.change ?? 0,
        sparkline: metric.sparkline ?? [],
        compare: metric.amount
            ? NumeralMethods.cash(metric.amount.value ?? 0)
            : '',
    })) : []

    $: filteredActivities = groupFilter === 'all'
        ? activities
        : activities.filter((a) => a.group === groupFilter)

    $: activityRows = filteredActivities.flatMap((activity) => {
        const rows = [{
            key: activity.name,
            label: activity.label,
            value: NumeralMethods.number(activity.value ?? 0),
            change: activity.change ?? 0,
            sparkline: activity.sparkline ?? [],
        }]

        if (activity.amount) {
            rows.push({
                key: `${activity.name}.amount`,
                label: `${activity.label} (MXN)`,
                value: NumeralMethods.cash(activity.amount.value ?? 0),
                change: activity.amount.change ?? 0,
                sparkline: activity.amount.sparkline ?? [],
            })
        }

        return rows
    })

    $: empty = !loading && !error && highlights && !activities.length
        && !(highlights.quotations?.value || highlights.shipments?.value
            || highlights.shipmentErrors?.value || highlights.payments?.value)

    $: load(dateStart, dateEnd)
    $: {
        if (chartContainer) syncChart(chartContainer, series)
        else disposeChart()
    }

    onDestroy(disposeChart)

    const SPARK_W = 64
    const SPARK_H = 28
    const SPARK_PAD = 3
    const SPARK_ACCENT = '#22c55e'

    function buildSparkline(values) {
        if (!values?.length) return { line: '', area: '' }

        const min = Math.min(...values)
        const max = Math.max(...values)
        const range = max - min || 1

        const points = values.map((v, i) => {
            const x = values.length === 1 ? SPARK_W / 2 : (i / (values.length - 1)) * SPARK_W
            const y = SPARK_PAD + (1 - (v - min) / range) * (SPARK_H - SPARK_PAD * 2)
            return { x, y }
        })

        const line = points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ')
        const last = points[points.length - 1]
        const area = `${line} L${last.x},${SPARK_H} L${points[0].x},${SPARK_H} Z`

        return { line, area }
    }

    function onResize() {
        chart?.resize()
    }

    function disposeChart() {
        window.removeEventListener('resize', onResize)
        chart?.dispose()
        chart = null
    }

    async function load(start, end) {
        if (!start || !end) return

        loading = true
        error = null

        const res = await StatisticsService.getEventsActivity({
            dateStart: start.toISOString(),
            dateEnd: end.toISOString(),
        })

        loading = false

        if (!res?.success) {
            error = res?.error?.message || 'No se pudo cargar la actividad'
            highlights = null
            activities = []
            series = null
            return
        }

        const data = res.data ?? {}
        highlights = data.highlights ?? null
        activities = data.activities ?? []
        series = data.series ?? null
    }

    function syncChart(el, data) {
        if (chart && chart.getDom() !== el) disposeChart()

        if (!chart) {
            chart = echarts.init(el)
            window.addEventListener('resize', onResize)
        }

        if (!data?.labels || !data?.datasets) {
            chart.clear()
            return
        }

        chart.setOption({
            color: data.datasets.map((ds) => SERIES_COLORS[ds.name] || '#9ca3af'),
            tooltip: { trigger: 'axis' },
            legend: {
                top: 0,
                right: 0,
                icon: 'circle',
                itemWidth: 8,
                itemHeight: 8,
                textStyle: { color: '#6b7280', fontSize: 12 },
            },
            grid: {
                left: 8,
                right: 8,
                top: 40,
                bottom: 8,
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: data.labels,
                axisLine: { lineStyle: { color: '#e5e7eb' } },
                axisTick: { show: false },
                axisLabel: { color: '#9ca3af', fontSize: 11 },
            },
            yAxis: {
                type: 'value',
                splitLine: { lineStyle: { color: '#f3f4f6' } },
                axisLabel: { color: '#9ca3af', fontSize: 11 },
            },
            series: data.datasets.map((ds) => ({
                name: ds.label,
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 8,
                showSymbol: data.labels.length <= 14,
                lineStyle: { width: 2.5 },
                areaStyle: { opacity: 0.08 },
                itemStyle: { borderWidth: 2, borderColor: '#fff' },
                data: ds.data,
            })),
        }, true)
    }

</script>

<div class="activity">
    <div class="header">
        <h3 class="title">Actividad de usuarios</h3>
        {#if periodLabel}
            <p class="period">{ periodLabel }</p>
        {/if}
    </div>

    <Spinner { loading } />

    {#if error && !highlights}
        <div class="card">
            <p class="state is-error">{ error }</p>
        </div>
    {:else if empty}
        <div class="card">
            <p class="state">Sin actividad registrada en el periodo</p>
        </div>
    {:else if highlights}
        <div class="highlights">
            {#each highlightCards as card}
                <StatsCard
                    icon=""
                    title={card.title}
                    value={card.value}
                    change={card.change}
                    sparkline={card.sparkline}
                    compare={card.compare}
                />
            {/each}
        </div>

        <div class="panels">
            <div class="card">
                <h4 class="card-title">Actividad diaria</h4>
                <div bind:this={chartContainer} class="chart"></div>
            </div>

            <div class="card">
                <div class="list-header">
                    <h4 class="card-title">Actividades</h4>
                    <Tabs options={GROUP_TABS} bind:selected={groupFilter} size="small" />
                </div>

                {#if activityRows.length}
                    <ul class="activities">
                        {#each activityRows as row, i (row.key)}
                            {@const spark = buildSparkline(row.sparkline)}
                            {@const isUp = row.change >= 0}
                            <li class="activity-row">
                                <span class="rank">{ i + 1 }</span>
                                <span class="label">{ row.label }</span>
                                <span class="value">{ row.value }</span>
                                <span class="change" class:is-down={!isUp}>
                                    <i class="fas {isUp ? 'fa-arrow-up' : 'fa-arrow-down'}"></i>
                                    { Math.abs(row.change) }%
                                </span>
                                {#if spark.line}
                                    <svg class="sparkline" viewBox="0 0 {SPARK_W} {SPARK_H}" aria-hidden="true">
                                        <path d={spark.area} fill={SPARK_ACCENT} fill-opacity="0.15" />
                                        <path d={spark.line} fill="none" stroke={SPARK_ACCENT} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
                                    </svg>
                                {:else}
                                    <span class="sparkline"></span>
                                {/if}
                            </li>
                        {/each}
                    </ul>
                {:else}
                    <p class="state">Sin actividades en este grupo</p>
                {/if}
            </div>
        </div>
    {/if}
</div>

<style>
    .activity {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        width: 100%;
    }

    .header {
        margin-bottom: 0;
    }

    .title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0;
        line-height: 1.2;
    }

    .period {
        font-size: 0.85rem;
        color: #9e9e9e;
        margin: 0.25rem 0 0;
        text-transform: capitalize;
    }

    .panels {
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        gap: 1.5rem;
        align-items: stretch;
    }

    .highlights {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 1.5rem;
    }

    .card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
        padding: 1.35rem 1.5rem;
        min-height: 360px;
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .card-title {
        font-size: 1rem;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 0.85rem;
    }

    .chart {
        width: 100%;
        height: 300px;
        flex: 1;
    }

    .list-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        margin-bottom: 0.85rem;
        flex-wrap: wrap;
    }

    .list-header .card-title {
        margin: 0;
    }

    .list-header :global(.tabs) {
        margin-bottom: 0;
    }

    .list-header :global(.tabs ul) {
        border-bottom-color: transparent;
    }

    .activities {
        list-style: none;
        margin: 0;
        padding: 0;
        overflow: auto;
        flex: 1;
        min-height: 0;
    }

    .activity-row {
        display: grid;
        grid-template-columns: 1.5rem minmax(0, 1fr) auto auto 4rem;
        align-items: center;
        gap: 0.65rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid #f5f5f5;
    }

    .activity-row:last-child {
        border-bottom: none;
    }

    .rank {
        color: #9e9e9e;
        font-size: 0.85rem;
    }

    .label {
        font-size: 0.9rem;
        font-weight: 500;
        color: #1a1a1a;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .value {
        font-size: 0.95rem;
        font-weight: 700;
        color: #1a1a1a;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .change {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: #22c55e;
        white-space: nowrap;
    }

    .change.is-down {
        color: #ef4444;
    }

    .change :global(i) {
        font-size: 0.65rem;
    }

    .sparkline {
        width: 64px;
        height: 28px;
        justify-self: end;
    }

    .state {
        padding: 2.5rem 1rem;
        text-align: center;
        color: #9e9e9e;
        font-size: 0.95rem;
    }

    .state.is-error {
        color: #ef4444;
    }

    @media (max-width: 1024px) {
        .highlights {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .panels {
            grid-template-columns: 1fr;
        }
    }
</style>